// ontologyLoader.js
import yaml from "js-yaml";

// Private file paths (not exported)
const SESSION_YAML = "../ontology/chatSession.yaml";
const PROFILE_YAML = "../ontology/profile.yaml";
const PROJECT_YAML = "../ontology/project.yaml";
const ROOM_YAML = "../ontology/room.yaml";

// Internal cache
const _cache = new Map();

// Private name → path map
const PATHS = {
  profile: PROFILE_YAML,
  project: PROJECT_YAML,
  room: ROOM_YAML,

  // kept internal; treated as private/blocked below
  session: SESSION_YAML,
  sessions: SESSION_YAML,
  chatsession: SESSION_YAML,
};

function normalizeName(name) {
  return String(name || "")
    .trim()
    .toLowerCase();
}

/**
 * Load user.yaml (works in browser or Node).
 * @param {string} ontologyPath path/URL to user.yaml
 * @returns {Promise<object>} parsed YAML object
 */
export async function loadOntology(ontology) {
  // Browser: fetch from public assets

  const ontologyPath = PATHS[ontology];

  if (typeof window !== "undefined" && typeof fetch === "function") {
    const res = await fetch(ontologyPath, { cache: "no-store" });
    if (!res.ok)
      throw new Error(`Failed to fetch ${ontologyPath}: ${res.status}`);
    const text = await res.text();

    return yaml.load(text);
  }

  // Node: read from filesystem
  const res = await fetch(url, { cache: "no-store" });
  const content = await res.text();

  return yaml.load(content);
}

// ── Tiny UUID helper (no deps) ───────────────────────────────────────────────
const uuidv4 = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

// Extract {TOKENS} from a template
function extractTokens(str = "") {
  const re = /{([A-Z0-9_]+)}/gi;
  const out = new Set();
  let m;
  while ((m = re.exec(str))) out.add(m[1]);
  return [...out];
}

// Replace {TOKENS} using a map; throw if missing
function materialize(template, map) {
  return String(template).replace(/{([A-Z0-9_]+)}/gi, (_, t) => {
    if (map[t] == null) throw new Error(`No mapping for token {${t}}`);
    return String(map[t]);
  });
}

// Default value based on a light JSON Schema-ish hint
function defaultForProperty(schema = {}) {
  if ("default" in schema) return schema.default;
  if (schema.format === "date-time") return new Date().toISOString();
  switch (schema.type) {
    case "array":
      return [];
    case "object":
      return {};
    case "number":
    case "integer":
      return 0;
    case "boolean":
      return false;
    default:
      return "";
  }
}

// Generate id from id_pattern (supports {UUID},{COGNITO_ID},{TIMESTAMP}, plus custom vars)
function generateIdFromPattern(idPattern, { awsCreds, vars } = {}) {
  if (!idPattern) return uuidv4();
  const tokenMap = {
    UUID: uuidv4(),
    COGNITO_ID: awsCreds?.identityId || "",
    TIMESTAMP: Date.now().toString(),
    ...(vars || {}),
  };
  return materialize(idPattern, tokenMap);
}
// OntologyUtils.js

// Assumes these helpers already exist in your file:
// - generateIdFromPattern(idPattern, { awsCreds, vars })
// - defaultForProperty(schema)

function humanizeTitle(s) {
  return String(s || "")
    .replace(/[_\-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export function buildEntityFromOntology(meta, { awsCreds, vars } = {}) {
  const now = new Date().toISOString();
  const props = meta?.properties || {};
  const required = Array.isArray(meta?.required) ? meta.required : [];

  // Copy vars so we can derive sensible defaults without mutating caller's object
  const effectiveVars = { ...(vars || {}) };

  // If ROOM_ID is missing and ontology defines kind enum, use its first option
  // so id_pattern like "room:{PROJECT_UUID}:{ROOM_ID}" works out of the box.
  const kindEnum = props?.kind?.enum;
  if (
    (effectiveVars.ROOM_ID == null || effectiveVars.ROOM_ID === "") &&
    Array.isArray(kindEnum) &&
    kindEnum.length > 0
  ) {
    effectiveVars.ROOM_ID = kindEnum[0]; // e.g., "bedroom"
  }

  // Build id using effective vars (may include the derived ROOM_ID)
  const id = generateIdFromPattern(meta?.id_pattern, {
    awsCreds,
    vars: effectiveVars,
  });
  const obj = { id };

  // Populate required fields
  for (const name of required) {
    if (name === "id") continue;

    if (name === "createdAt") {
      obj.createdAt = now;
      continue;
    }
    if (name === "updatedAt") {
      obj.updatedAt = now;
      continue;
    }

    const schema = props[name] || {};
    let value;

    // 1) Context-driven values from vars
    // Map common IDs straight from context if available.
    if (name === "projectId" && effectiveVars.PROJECT_UUID) {
      // Assume PROJECT_UUID already includes any prefix (e.g., "project_...") per your app
      value = effectiveVars.PROJECT_UUID;
    } else if (name === "roomId" && effectiveVars.ROOM_ID) {
      value = effectiveVars.ROOM_ID;
    }

    // 2) If property has an enum, prefer its first value
    if (value == null && Array.isArray(schema.enum) && schema.enum.length > 0) {
      value = schema.enum[0];
    }

    // 3) If this looks like a status and FSM defines states, use the first state as fallback
    if (
      value == null &&
      name === "status" &&
      Array.isArray(meta?.fsm?.states) &&
      meta.fsm.states.length > 0
    ) {
      value = meta.fsm.states[0];
    }

    // 4) Schema-based default (type/default/format)
    if (value == null) {
      value = defaultForProperty(schema);
    }

    obj[name] = value;
  }

  // Post-processing:
  // If title is required/exists but empty, derive from kind or ROOM_ID (e.g., "bedroom" -> "Bedroom")
  if (
    (required.includes("title") ||
      Object.prototype.hasOwnProperty.call(props, "title")) &&
    !obj.title
  ) {
    const source = obj.kind ?? effectiveVars.ROOM_ID;
    if (source) obj.title = humanizeTitle(source);
  }

  // If "type" is defined in schema but wasn't set, default to ontology name
  if (props.type && obj.type == null) {
    obj.type = meta?.name || "entity";
  }

  return obj;
}

/**
 * Build an S3 key from a {TOKEN}-style template.
 * Always returns a string (or throws if a required token is missing).
 */
/**
 * Build an S3 key from a {TOKEN}-style template.
 * Always returns a string (or throws if a required token is missing).
 */
export function keyFromTemplate(template, params = {}) {
  if (typeof template !== "string" || template.length === 0) {
    throw new TypeError("keyFromTemplate: template must be a non-empty string");
  }

  const { entity = null, awsCreds = {}, vars = {} } = params;

  const tokenMap = {
    // common tokens
    COGNITO_ID: awsCreds.identityId ?? "",
    UUID: uuidv4(),
    TIMESTAMP: String(Date.now()),

    // optional entity-based tokens (only added if present)
    ...(entity?.id ? { ID: entity.id, id: entity.id } : {}),

    // caller-supplied tokens override everything above
    ...vars,
  };

  // Replace {TOKEN} with string values; throw if any token is missing
  let key = template.replace(/\{([A-Z0-9_]+)\}/g, (_, name) => {
    const val = tokenMap[name];
    if (val === undefined || val === null) {
      throw new Error(`keyFromTemplate: missing token "${name}"`);
    }
    return String(val);
  });

  // If template ends with "/", append a filename
  if (key.endsWith("/")) {
    key += `${entity?.id ? entity.id : uuidv4()}.json`;
  }

  return key;
}
